import * as d3 from "d3";

interface Node extends d3.SimulationNodeDatum {
  id: string;
  group: number;
}

interface Link extends d3.SimulationLinkDatum<Node> {
  source: string | Node;
  target: string | Node;
  value: number;
}

interface GraphData {
  nodes: Array<{ id?: string; name?: string; group: number }>;
  links: Array<{
    source: number | string;
    target: number | string;
    value: number;
  }>;
}

interface NormalizedGraphData {
  nodes: Node[];
  links: Link[];
}

// Get container dimensions
const container = document.getElementById("graph") as HTMLElement;
const width = container.clientWidth;
const height = container.clientHeight;

// Create SVG
const svg = d3
  .select("#graph")
  .append("svg")
  .attr("width", width)
  .attr("height", height);

// Create color scale
const color = d3.scaleOrdinal<number, string>(d3.schemeCategory10);

// Create simulation
const simulation = d3
  .forceSimulation<Node>()
  .force(
    "link",
    d3.forceLink<Node, Link>().id((d) => d.id),
  )
  .force("charge", d3.forceManyBody())
  .force("center", d3.forceCenter(width / 2, height / 2));

// Create groups for links and nodes
const link = svg.append("g").attr("class", "links").selectAll("line");
const node = svg.append("g").attr("class", "nodes").selectAll("circle");
const hoverLabel = svg
  .append("text")
  .attr("class", "hover-label")
  .attr("font-size", 12)
  .attr("font-weight", 600)
  .attr("fill", "#222")
  .attr("text-anchor", "start")
  .attr("pointer-events", "none")
  .style("display", "none");

const svgNode = svg.node() as SVGSVGElement;

// Load and render data
fetch("https://bost.ocks.org/mike/miserables/miserables.json")
  .then((response) => response.json())
  .then((rawGraph: GraphData) => {
    // The remote dataset uses node.name and numeric link indices.
    // Normalize to id-based nodes and links before wiring to forces.
    const graph: NormalizedGraphData = {
      nodes: rawGraph.nodes.map((n, i) => ({
        id: n.id ?? n.name ?? String(i),
        group: n.group ?? 0,
      })),
      links: [],
    };

    graph.links = rawGraph.links.map((l) => ({
      source:
        typeof l.source === "number"
          ? (graph.nodes[l.source]?.id ?? String(l.source))
          : String(l.source),
      target:
        typeof l.target === "number"
          ? (graph.nodes[l.target]?.id ?? String(l.target))
          : String(l.target),
      value: l.value ?? 1,
    }));

    // Update links
    const linkSelection = link
      .data(graph.links)
      .enter()
      .append("line")
      .attr("stroke", "#999")
      .attr("stroke-opacity", 0.6)
      .attr("stroke-width", (d) => Math.sqrt(d.value));

    // Update nodes
    const nodeSelection = node
      .data(graph.nodes)
      .enter()
      .append("circle")
      .attr("r", 5)
      .attr("fill", (d) => color(d.group))
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5)
      .on("mouseenter", (event, d) => {
        const [x, y] = d3.pointer(event, svgNode);
        hoverLabel
          .text(d.id)
          .attr("x", x + 10)
          .attr("y", y + 4)
          .style("display", "block");
      })
      .on("mousemove", (event) => {
        const [x, y] = d3.pointer(event, svgNode);
        hoverLabel.attr("x", x + 10).attr("y", y + 4);
      })
      .on("mouseleave", () => {
        hoverLabel.style("display", "none");
      })
      .call(
        d3
          .drag<SVGCircleElement, Node>()
          .on("start", dragstarted)
          .on("drag", dragged)
          .on("end", dragended) as any,
      );

    // Add titles
    nodeSelection.append("title").text((d) => d.id);

    // Update simulation
    simulation.nodes(graph.nodes);
    (simulation.force("link") as d3.ForceLink<Node, Link>).links(graph.links);

    // Update positions on tick
    simulation.on("tick", () => {
      linkSelection
        .attr("x1", (d) =>
          typeof d.source === "object" ? (d.source as Node).x || 0 : 0,
        )
        .attr("y1", (d) =>
          typeof d.source === "object" ? (d.source as Node).y || 0 : 0,
        )
        .attr("x2", (d) =>
          typeof d.target === "object" ? (d.target as Node).x || 0 : 0,
        )
        .attr("y2", (d) =>
          typeof d.target === "object" ? (d.target as Node).y || 0 : 0,
        );

      nodeSelection.attr("cx", (d) => d.x || 0).attr("cy", (d) => d.y || 0);
    });
  })
  .catch((error) => console.error("Error loading data:", error));

function dragstarted(event: d3.D3DragEvent<SVGCircleElement, Node, Node>) {
  if (!event.active) simulation.alphaTarget(0.3).restart();
  event.subject.fx = event.subject.x;
  event.subject.fy = event.subject.y;
}

function dragged(event: d3.D3DragEvent<SVGCircleElement, Node, Node>) {
  event.subject.fx = event.x;
  event.subject.fy = event.y;
}

function dragended(event: d3.D3DragEvent<SVGCircleElement, Node, Node>) {
  if (!event.active) simulation.alphaTarget(0);
  event.subject.fx = null;
  event.subject.fy = null;
}
